import React from 'react';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { useDisconnectIntegration } from '../../api/useDisconnectIntegrationMutation';
import { IntegrationDTO, StatusDTO } from '../../api/useIntegrationsSettingsQuery';
import { IntegrationInfo } from '../IntegrationInfo/IntegrationInfo';
import { useWealthbox } from './useWealthbox';

const website = 'https://www.wealthbox.com/';
const helpUrl =
  'https://orionadvisorservices.my.site.com/OrionSupportApp/s/article/How-to-import-clients-from-Wealthbox-into-Orion-Planning';

interface Props {
  integration: IntegrationDTO;
  onClose(): void;
}

export const Wealthbox = ({ integration, onClose }: Props) => {
  const { connectWealthbox, error: connectError, isLoading: isConnecting } = useWealthbox();

  const {
    mutateAsync: disconnectIntegration,
    error: disconnectError,
    isLoading: isDisconnecting,
  } = useDisconnectIntegration();

  return (
    <Modal isOpen onRequestClose={onClose} aria={{ labelledby: 'modal-header' }}>
      <ModalHeader as="h2" id="modal-header">
        {integration.Label}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <IntegrationInfo integration={integration} website={website} helpUrl={helpUrl} />

        {connectError && (
          <Alert
            variant="error"
            message="There was an issue completing your request. Please try again later."
          />
        )}
        {disconnectError && <Alert variant="error" message={disconnectError.message} />}

        {integration.Status === StatusDTO.Success && (
          <Box display="flex" flexDirection="column" css={{ gap: '8px' }} mt="16">
            <Box display="flex" alignItems="center" css={{ gap: '8px' }}>
              <Text fontSize="h5">Connected</Text>
              <Icon className="fas fa-check" color="success.500" />
            </Box>

            <Text>Your {integration.Label} account is connected to Orion Planning.</Text>
          </Box>
        )}

        {integration.Status !== StatusDTO.Success && (
          <Text>Import your contacts from Wealthbox into Orion Planning.</Text>
        )}
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        {integration.Status === StatusDTO.Success ? (
          <Button
            ml="8"
            onClick={async () => {
              try {
                await disconnectIntegration(integration.Name);
                onClose();
              } catch (err) {
                console.error('Error disconnecting integration:', err);
              }
            }}
            disabled={isDisconnecting}
            isLoading={isDisconnecting}
          >
            Disconnect {integration.Label}
          </Button>
        ) : (
          <Button
            ml="8"
            onClick={() => connectWealthbox()}
            disabled={isConnecting}
            isLoading={isConnecting}
            rightIcon="fa fa-download"
          >
            Connect to {integration.Label}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
